import {NodeFormBuilder} from "../../../../../../../shared/models";
import {FormControl, FormGroup} from "@angular/forms";
import {required} from "../../../../../../../shared/utils";
import {newStepValue} from '../../../../../services/flowBuilder/utils/constants';
import {RCS} from "../../../../../../../shared/utils/config";

export const NODE_TYPE = "sendRichText";

/** The SendRichTextForm.
 * It is the flattened version of the step.
 * It should contain all the fields that can be changed by the flow-builder.
 */
export interface SendRichTextForm {
    name: string;
    success: string;
    error: string;
    description: string;
    sendTo: string;
    integrationId: string;
    apiKey: string; // TO BE REMOVED ONCE BACKEND SUPPORTS INTEGRATIONID ONLY
    channelID: string;
    referenceId: string;
    bodyText: string;
    endSession: boolean;
    imageRef: string;
    header: string;
    items: Item[];
}

export interface Item {
    text: string,
    additionalData: string,
    next: string
}

export class SendRichText extends NodeFormBuilder<SendRichTextForm> {
    /**
     * NodeFormBuilder will set up the correct step type.
     */
    constructor() {
        super("action", NODE_TYPE);
    }

    /**
     * Takes in stepData and returns the flattened version

     * @return SendRichTextForm
     * @param stepData
     */
    getFromData(stepData: { [index: string]: any }): SendRichTextForm {

        const action = stepData?.action ? stepData?.action : {};
        const result = action?.result ? action?.result : {};

        return {
            name: this.getValueOrDefault(action?.name),
            success: this.getValueOrDefault(result?.success, newStepValue),
            error: this.getValueOrDefault(result?.error, newStepValue),
            description: this.getValueOrDefault(action?.description),
            sendTo: this.getValueOrDefault(action?.actionParams?.sendTo),
            integrationId: this.getValueOrDefault(action?.actionParams?.integrationId),
            apiKey: this.getValueOrDefault(action?.actionParams?.apiKey),  // TO BE REMOVED ONCE BACKEND SUPPORTS INTEGRATIONID ONLY
            channelID: this.getValueOrDefault(action?.actionParams?.channelID, RCS),
            referenceId: this.getValueOrDefault(action?.actionParams?.parameters?.referenceId),
            bodyText: this.getValueOrDefault(action?.actionParams?.parameters?.bodyText),
            endSession: this.getValueOrDefault(action?.actionParams?.parameters?.endSession, false),
            imageRef: this.getValueOrDefault(action?.actionParams?.parameters?.imageRef),
            header: this.getValueOrDefault(action?.actionParams?.parameters?.header),
            items: action?.actionParams?.parameters?.items ? action.actionParams.parameters.items.map((item: {
                [index: string]: any
            }) => ({
                text: this.getValueOrDefault(item.text),
                additionalData: this.getValueOrDefault(item.additionalData),
                next: this.getValueOrDefault(item.next)
            })) : []
        }
    }

    /**
     * Constructs a FormGroup using the passed in FoobarForm
     * Only users with the correct roll is allowed to edit a flow so fields will be locked.
     *
     * @return FormGroup
     * @param formData
     * @param editFlows
     */
    buildForm(formData: SendRichTextForm, editFlows: boolean): FormGroup {

        return this.formBuilder.group({
            name: new FormControl({
                value: formData.name,
                disabled: !editFlows,
            }, required("Name")),
            success: new FormControl({
                value: formData.success,
                disabled: !editFlows
            }),
            error: new FormControl({
                value: formData.error,
                disabled: !editFlows
            }),
            description: new FormControl({
                value: formData.description,
                disabled: !editFlows,
            }, required("Description")),
            sendTo: new FormControl({
                value: formData.sendTo,
                disabled: !editFlows,
            }, required("Send To")),
            integrationId: new FormControl({
                value: formData.integrationId,
                disabled: !editFlows,
            }, required("Integration ID")),
            // TO BE REMOVED ONCE BACKEND SUPPORTS INTEGRATIONID ONLY
            apiKey: new FormControl({
                value: formData.apiKey,
                disabled: !editFlows,
            }, required("Api Key")),
            //
            channelID: new FormControl({
                value: formData.channelID,
                disabled: !editFlows,
            }, required("Channel ID")),
            referenceId: new FormControl({
                value: formData.referenceId,
                disabled: !editFlows,
            }),
            bodyText: new FormControl({
                value: formData.bodyText,
                disabled: !editFlows,
            }, required("Body Text")),
            endSession: new FormControl({
                value: formData.endSession,
                disabled: !editFlows,
            }),
            imageRef: new FormControl({
                value: formData.imageRef,
                disabled: !editFlows,
            }),
            header: new FormControl({
                value: formData.header,
                disabled: !editFlows,
            }),
            items: this.formBuilder.array(formData.items.map(item => this.itemTemplate(item, editFlows)))
        });

    }

    /**
     * Reconstructs the step from the passed in SendRichTextForm
     *
     * @param nodeData
     */
    setNodeData(nodeData: SendRichTextForm) {

        const {
            name,
            success,
            error,
            description,
            sendTo,
            bodyText,
            channelID,
            items,
            referenceId,
            integrationId,
            apiKey,
            endSession,
            imageRef,
            header
        } = nodeData;

        const actionParams = {
            sendTo,
            integrationId,
            apiKey,
            channelID,
            parameters: {
                referenceId,
                header,
                bodyText,
                endSession,
                items,
                imageRef
            }
        }

        const result = {
            success,
            error,
        }

        if (endSession) {
            delete result['success'];
        }

        this.action = {
            ...this.action,
            name,
            description,
            actionParams,
            result
        }
    }

    itemTemplate(item: Item, editFlows: boolean): FormGroup {
        return this.formBuilder.group({
            text: new FormControl({
                value: item.text,
                disabled: !editFlows
            }, required("Text")),
            additionalData: new FormControl({
                value: item.additionalData,
                disabled: !editFlows
            }),
            next: new FormControl({
                value: item.next,
                disabled: !editFlows
            })
        })
    }

}

// # step1:
// #   action:
// #     name: "name"
// #     description: "description"
// #     actionType: "sendRichText"
// #     actionParams:
// #       sendTo: "[[start:input:body:number]]"
// #       integrationId: "UUID"
// #       channelID: "12"
// #       parameters:
// #         referenceId: "" // Optional
// #         header: ""
// #         bodyText: ""
// #         endSession: bool
// #         imageRef: ""
// #         items:
// #         - text: "Yes"
// #           additionalData: ""
// #           next: "step1"
// #         - text: "No"
// #           additionalData: ""
// #           next: "step2"
// #     result:
// #       success: "step2"
// #       error: "step2"
// #   type: "action"